import React, { useState } from 'react';
import apiFetch from '../../../../utils/api';
import { useStats } from '../../../../context/StatsContext';
import { formatCurrencyPKR } from '../../../../utils/format';
import Preloader from '../../../../components/Preloader/Preloader';
import './RewardList.css';

const RewardList = () => {
  const { rewards: data, loadingRewards: loading, refreshRewards } = useStats();
  const [claimingId, setClaimingId] = useState(null);
  const [claimMsg, setClaimMsg] = useState(null);

  const handleClaim = async (levelId) => {
    setClaimingId(levelId);
    setClaimMsg(null);
    try {
      const res = await apiFetch('/api/rewards/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rewardId: levelId }),
      });
      const d = await res.json();
      setClaimMsg({ error: !d.success, text: d.message });
      if (d.success) refreshRewards();
    } catch {
      setClaimMsg({ error: true, text: 'Server error.' });
    }
    setClaimingId(null);
  };

  if (loading) {
    return (
      <div className="rl-page">
        <Preloader message="Fetching your rewards..." />
      </div>
    );
  }

  if (!data) {
    return <div className="rl-page"><div className="rl-empty">Failed to load rewards.</div></div>;
  }

  const { levels: rawLevels, pairCount, pinsUsed, currentRank, nextLevel } = data;

  // [DATA INTEGRITY] Sort levels: Move "Starter" (Tier 14) to the very top
  const levels = [...rawLevels].sort((a, b) => {
    if (a.rank === 'Starter') return -1;
    if (b.rank === 'Starter') return 1;
    return 0; // Keep others in existing order (Level 1, 2, etc.)
  });


  return (
    <div className="rl-page">
      {/* Header */}
      <div className="rl-header">
        <div>
          <h1 className="rl-header__title">Alpha Bonuses</h1>
          <p className="rl-header__sub">Build your team, unlock rewards, earn one-time bonuses</p>
        </div>
      </div>

      {/* Guide */}
      <div className="rl-guide">
        <div className="rl-guide__icon">📖</div>
        <div className="rl-guide__content">
          <h3>How Rewards Work</h3>
          <ol>
            <li><strong>Build your team</strong> — Add members on both your left and right sides.</li>
            <li><strong>Meet the requirement</strong> — Each level requires a certain number of matching left-right pairs.</li>
            <li><strong>Unlock the level</strong> — Click the "Unlock" button when you're eligible.</li>
            <li><strong>Wait for Admin Payment</strong> — Once claimed, our team will verify your claim and credit the bonus one time.</li>
          </ol>
          <p className="rl-guide__note">💡 You can claim multiple levels! Each level gives a one-time bonus after admin approval.</p>
        </div>
      </div>

      {/* Progress Hero */}
      <div className="rl-hero">
        <div className="rl-hero__rank">
          <div className="rl-hero__rank-icon">🏆</div>
          <div>
            <div className="rl-hero__rank-label">Current Rank</div>
            <div className="rl-hero__rank-name">{currentRank}</div>
          </div>
        </div>
        <div className="rl-hero__stats">
          <div className="rl-hero__stat">
            <span className="rl-hero__stat-num">{pairCount}</span>
            <span className="rl-hero__stat-label">Matching Pairs</span>
          </div>
          <div className="rl-hero__stat">
            <span className="rl-hero__stat-num">{pinsUsed}</span>
            <span className="rl-hero__stat-label">Pins Used</span>
          </div>
          <div className="rl-hero__stat">
            <span className="rl-hero__stat-num">{levels.filter(l => l.claimed).length}</span>
            <span className="rl-hero__stat-label">Levels Claimed</span>
          </div>
        </div>
        {nextLevel && (
          <div className="rl-hero__next">
            <div className="rl-hero__next-text">
              Next: <strong>{nextLevel.rank}</strong> — need {nextLevel.required} pairs
            </div>
            <div className="rl-hero__bar-wrap">
              <div
                className="rl-hero__bar-fill"
                style={{ width: `${Math.min((pairCount / nextLevel.required) * 100, 100)}%` }}
              />
            </div>
            <div className="rl-hero__bar-label">
              {pairCount} / {nextLevel.required} pairs
            </div>
          </div>
        )}
      </div>

      {/* Claim message */}
      {claimMsg && (
        <div className={`rl-claim-msg ${claimMsg.error ? 'rl-claim-msg--error' : 'rl-claim-msg--success'}`}>
          {claimMsg.text}
        </div>
      )}

      {/* Level Cards */}
      <div className="rl-levels">
        {levels.map((level, i) => (
          <div
            key={level.id || i}
            className={`rl-level ${level.claimed ? 'rl-level--claimed' : level.unlocked ? 'rl-level--unlocked' : 'rl-level--locked'}`}
          >
            <div className="rl-level__num">{i + 1}</div>
            <div className="rl-level__body">
              <div className="rl-level__top">
                <h3 className="rl-level__rank">{level.rank}</h3>
                {level.claimed ? (
                  level.paymentStatus === 'Paid' ? (
                    <span className="rl-level__badge rl-level__badge--claimed">✅ Paid</span>
                  ) : (
                    <span className="rl-level__badge rl-level__badge--pending">⏳ Payment Pending</span>
                  )
                ) : level.canClaim ? (
                  <button
                    className="rl-level__unlock-btn"
                    onClick={() => handleClaim(level.id)}
                    disabled={claimingId === level.id}
                  >
                    {claimingId === level.id ? '⏳ Claiming...' : '🔓 Unlock'}
                  </button>
                ) : level.unlocked ? (
                  <span className="rl-level__badge rl-level__badge--unlocked">✓ Eligible</span>
                ) : (
                  <span className="rl-level__badge rl-level__badge--locked">🔒 Locked</span>
                )}
              </div>
              <div className="rl-level__details">
                <div className="rl-level__detail">
                  <span className="rl-level__detail-label">Pairs Required</span>
                  <span className="rl-level__detail-value">{level.required} Pairs</span>
                </div>
                <div className="rl-level__detail">
                  <span className="rl-level__detail-label">Bonus Amount</span>
                  <span className="rl-level__detail-value rl-level__reward">Rs. {formatCurrencyPKR(level.bonusAmount)}</span>
                </div>
                {level.claimed && level.claimInfo && (
                  <>
                    <div className="rl-level__detail">
                      <span className="rl-level__detail-label">Payment Status</span>
                      <span className={`rl-level__detail-value status-${(level.paymentStatus || 'Pending').toLowerCase()}`}>
                        {level.paymentStatus || 'Pending'}
                      </span>
                    </div>
                  </>
                )}
              </div>
              {!level.unlocked && (
                <div className="rl-level__progress">
                  <div className="rl-level__progress-bar">
                    <div
                      className="rl-level__progress-fill"
                      style={{ width: `${Math.min((pairCount / (level.required || 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="rl-level__progress-text">
                    {pairCount}/{level.required} pairs
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RewardList;